import { useEffect, useMemo } from 'react'

import { Button, Input, Label, Modal } from '@heroui/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller,useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { z } from 'zod'

import VehicleAutocomplete from '@/components/common/VehicleAutocomplete/VehicleAutocomplete'
import { Card, CardContent } from '@/components/ui/Card'
import { useDriversStore } from '@/store/drivers.store'

const driverSchema = z.object({
  fullName: z.string().min(2, 'Минимум 2 символа'),
  phone: z.string().min(7, 'Минимум 7 символов'),
  assignedVehicleId: z.string().optional(),
  active: z.string(),
})

type FormValues = z.infer<typeof driverSchema>;

interface DriverFormProps {
  driverId?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export function DriverForm({ driverId: propId, onSuccess, onCancel }: DriverFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id: routeId } = useParams<{ id: string }>()
  const id = propId || routeId
  const isEdit = Boolean(id)

  const { drivers, addDriver, updateDriver } = useDriversStore()

  const existing = id ? drivers.find(d => d.id === id) : undefined

  const initialVehicle = useMemo(() => existing?.assignedVehicle ? {
      ID: existing.assignedVehicle.id,
      Number: existing.assignedVehicle.plate,
      Model: existing.assignedVehicle.brand,
      Make: existing.assignedVehicle.year,
    } : null, [existing])

  const { register, handleSubmit, control, reset, formState: { errors, isSubmitting } } = useForm<FormValues>({
    resolver: zodResolver(driverSchema),
    defaultValues: existing
      ? {
        fullName: existing.fullName,
        phone: existing.phone ?? '',
        assignedVehicleId: existing.assignedVehicleId ?? 'none',
        active: existing.active ? 'true' : 'false',
      }
      : { fullName: '', phone: '', assignedVehicleId: 'none', active: 'true' },
  })

  useEffect(() => {
    if (existing) {
      reset({
        fullName: existing.fullName,
        phone: existing.phone ?? '',
        assignedVehicleId: existing.assignedVehicleId ?? 'none',
        active: existing.active ? 'true' : 'false',
      })
    } else {
      reset({ fullName: '', phone: '', assignedVehicleId: 'none', active: 'true' })
    }
  }, [existing, reset])

  const onSubmit = async (data: FormValues) => {
    const payload = {
      ...data,
      fullName: data.fullName.toUpperCase(),
      phone: data.phone || '',
      active: data.active === 'true',
    }

    try {
      if (isEdit && id) {
        await updateDriver(id, payload)
      } else {
        await addDriver(payload)
      }

      if (onSuccess) {
        onSuccess()
      } else {
        navigate('/drivers')
      }
    } catch (err) {
      console.error('Failed to save driver:', err)
    }
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    } else {
      navigate('/drivers')
    }
  }

  const formContent = (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 p-1">
      <div className="flex flex-col gap-1">
        <Label htmlFor="driver-fullName" isRequired>{t('drivers.full_name')}</Label>
        <Input
          id="driver-fullName"
          placeholder="ИВАНОВ ИВАН"
          className={errors.fullName ? 'border-danger' : ''}
          {...register('fullName')}
          variant={'secondary'}
        />
        {errors.fullName?.message && <span className="text-xs text-danger">{errors.fullName.message}</span>}
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor="driver-phone" isRequired>{t('drivers.phone')}</Label>
        <Input
          id="driver-phone"
          placeholder="+375 29 000-00-00"
          className={errors.phone ? 'border-danger' : ''}
          {...register('phone')}
          variant={'secondary'}
        />
        {errors.phone?.message && <span className="text-xs text-danger">{errors.phone.message}</span>}
      </div>

      <Controller
        name="assignedVehicleId"
        control={control}
        render={({ field }) => (
          <VehicleAutocomplete
            label={t('drivers.assigned_vehicle')}
            placeholder={t('drivers.no_vehicle')}
            selectedId={field.value}
            initialValue={initialVehicle}
            onSelectionChange={vehicle => field.onChange(vehicle?.ID || 'none')}
          />
        )}
      />

      {/*<Controller*/}
      {/*  name="active"*/}
      {/*  control={control}*/}
      {/*  render={({ field }) => (*/}
      {/*    <div className="flex flex-col gap-1">*/}
      {/*      <Label>{t('common.status')}</Label>*/}
      {/*      <Select*/}
      {/*        className="w-full"*/}
      {/*        selectedKey={field.value}*/}
      {/*        onSelectionChange={key => field.onChange(key)}*/}
      {/*      >*/}
      {/*        <Select.Trigger*/}
      {/*          className="flex items-center justify-between w-full px-3 py-2 border rounded-xl bg-background hover:bg-default-100 transition-colors"*/}
      {/*        >*/}
      {/*          <Select.Value />*/}
      {/*          <Select.Indicator />*/}
      {/*        </Select.Trigger>*/}
      {/*        <Select.Popover className="min-w-[200px] p-1 bg-background border rounded-xl shadow-lg">*/}
      {/*          <ListBox>*/}
      {/*            <ListBox.Item id="true" textValue={t('common.active')}>*/}
      {/*              {t('common.active')}*/}
      {/*            </ListBox.Item>*/}
      {/*            <ListBox.Item id="false" textValue={t('common.inactive')}>*/}
      {/*              {t('common.inactive')}*/}
      {/*            </ListBox.Item>*/}
      {/*          </ListBox>*/}
      {/*        </Select.Popover>*/}
      {/*      </Select>*/}
      {/*    </div>*/}
      {/*  )}*/}
      {/*/>*/}

      <div className="flex gap-3 pt-4 justify-end">
        <Button variant="ghost" onClick={handleCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit"  isDisabled={isSubmitting}>
          {isSubmitting ? (
             <div className="flex items-center gap-2">
               <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                 <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                 <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
               </svg>
               {isEdit ? t('common.save') : t('drivers.add_driver')}
             </div>
          ) : (
            isEdit ? t('common.save') : t('drivers.add_driver')
          )}
        </Button>
      </div>
    </form>
  )

  if (onSuccess) {
    return formContent
  }

  return (
    <div className="p-6 max-w-lg space-y-5">
      <div className="flex items-center gap-3">
        <button onClick={handleCancel}
                className="p-1.5 rounded-lg transition-colors hover:bg-zinc-100 text-zinc-500 dark:hover:bg-zinc-800 dark:text-zinc-400"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
                  strokeLinejoin="round"
            />
          </svg>
        </button>
        <div>
          <h1 className="text-xl font-semibold text-zinc-800 dark:text-zinc-100">
            {isEdit ? t('drivers.edit_driver') : t('drivers.new_driver')}
          </h1>
        </div>
      </div>

      <Card>
        <CardContent className="">
          {formContent}
        </CardContent>
      </Card>
    </div>
  )
}

interface DriverDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  driverId?: string;
}

export default function DriverDialog({ isOpen, onOpenChange, driverId }: DriverDialogProps) {
  const { t } = useTranslation()

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <Modal.Backdrop variant="blur" isDismissable>
        <Modal.Container>
          <Modal.Dialog>
            <Modal.CloseTrigger />
            <Modal.Header>
              <Modal.Heading className={'font-bold'}>
                {driverId ? t('drivers.edit_driver') : t('drivers.new_driver')}
              </Modal.Heading>
            </Modal.Header>
            <Modal.Body className={'mt-4'}>
              <DriverForm
                key={driverId || 'new'}
                driverId={driverId}
                onSuccess={() => onOpenChange(false)}
                onCancel={() => onOpenChange(false)}
              />
            </Modal.Body>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  )
}
